import { useState, useEffect, useCallback } from 'react';

/**
 * 매출 기준 — 같은 원장 데이터를 다르게 집계할 뿐, 저장값은 바뀌지 않는다.
 *  - cash    : 결제일이 그 달인 이용권의 전액. 통장·카드정산·부가세와 대조되는 숫자
 *  - accrual : 이용기간에 일할 안분. 선결제를 한 달에 몰지 않아 매장 규모가 보인다
 */
export type RevenueBasis = 'cash' | 'accrual';

export interface TrendPoint {
  month: string;
  total: number;
  new_amount: number;
  renew_amount: number;
  count: number;
}

export interface PaymentMethodStat {
  method: string;
  total_amount: number;
  count: number;
}

export interface DiscountByReason {
  reason: string;
  total_discount: number;
  count: number;
}

export interface RevenueItem {
  id: string;
  user_id: string | null;
  user_name: string;
  name: string;
  price: number;
  original_price: number | null;
  discount_amount: number;
  payment_method: string;
  status: string;
  is_new: boolean;
  paid_on: string | null;
  start_date: string | null;
  end_date: string | null;
  month_amount: number;
}

export interface MonthlyRevenueSummary {
  revenue: number;
  prev_revenue: number;
  growth_rate: number | null;
  count: number;
  payer_count: number;
  avg_per_payer: number;
  new_count: number;
  new_amount: number;
  renew_count: number;
  renew_amount: number;
  discount_total: number;
  cancelled_count: number;
  cancelled_amount: number;
  deferred_balance: number;
}

export interface MonthlyRevenue {
  month: string;
  basis: RevenueBasis;
  summary: MonthlyRevenueSummary;
  trend: TrendPoint[];
  payment_methods: PaymentMethodStat[];
  discount_by_reason: DiscountByReason[];
  items: RevenueItem[];
}

const currentMonthKST = () => {
  const now = new Date(Date.now() + 9 * 60 * 60 * 1000);
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
};

export const shiftMonth = (month: string, delta: number) => {
  const [y, m] = month.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

export const useRevenueAnalytics = () => {
  const [month, setMonth] = useState(currentMonthKST);
  const [basis, setBasis] = useState<RevenueBasis>('cash');
  const [data, setData] = useState<MonthlyRevenue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { apiClient } = await import('../../../infrastructure/api');
      const res = await apiClient.get(
        `/v1/owner/payment-management/revenue/monthly?month=${month}&basis=${basis}&trend_months=12`
      );
      setData(res.data);
    } catch (e: any) {
      console.error('월별 매출 조회 실패:', e?.response?.status, e?.message);
      setError(e?.response?.data?.detail || '매출 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, [month, basis]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const thisMonth = currentMonthKST();

  return {
    month,
    setMonth,
    basis,
    setBasis,
    data,
    loading,
    error,
    isCurrentMonth: month >= thisMonth,
    goPrevMonth: () => setMonth((m) => shiftMonth(m, -1)),
    goNextMonth: () => setMonth((m) => (m >= thisMonth ? m : shiftMonth(m, 1))),
    refresh: fetchData,
  };
};
